import React, { useEffect, useMemo, useState, type FormEvent } from 'react';

import {
  createFileRoute,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { Button, TextBlock } from '@skillarea/lib-frontend';
import { UserUpdateDto, createValidator } from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import { accountAuthGuard } from './-account-auth-guard';
import styles from './dob.module.scss';

import { Container } from '~/components/Container/Container';
import { LayoutCard } from '~/components/LayoutCard/LayoutCard';
import { normalizeDobRedirectTarget } from '~/lib/auth/dobRouteLock';
import {
  useRequiresDateOfBirth,
  useSaveUserData,
  useUserDateOfBirth,
  useUserId,
} from '~/stores/userStore';

export const Route = createFileRoute('/account/dob')({
  component: DobPage,
  beforeLoad: ({ context }) => accountAuthGuard({ context }),
});

const userDataValidator = createValidator(UserUpdateDto);

const MONTH_VALUES = [
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
] as const;

function isSuccessfulResult(value: unknown): value is { success: true } {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return (value as { success?: unknown }).success === true;
}

function getValidationMessage(value: string): string | undefined {
  const result = userDataValidator({
    value: {
      dateOfBirth: value,
    },
  });

  if (!result || typeof result !== 'object') {
    return undefined;
  }

  const fieldResult = (result as { dateOfBirth?: unknown }).dateOfBirth;
  if (!fieldResult || typeof fieldResult !== 'object') {
    return undefined;
  }

  const errors = (fieldResult as { errors?: unknown }).errors;
  if (!Array.isArray(errors) || typeof errors[0] !== 'string') {
    return undefined;
  }

  return errors[0];
}

function getSaveErrorMessage(result: unknown): string | undefined {
  if (!result || typeof result !== 'object') {
    return undefined;
  }

  const dtoMessages = (result as { dtoMessages?: unknown }).dtoMessages;
  if (Array.isArray(dtoMessages)) {
    const firstDtoMessage = dtoMessages[0];
    if (
      firstDtoMessage &&
      typeof firstDtoMessage === 'object' &&
      typeof (firstDtoMessage as { message?: unknown }).message === 'string'
    ) {
      return (firstDtoMessage as { message: string }).message;
    }
  }

  const error = (result as { error?: unknown }).error;
  return typeof error === 'string' && error.length > 0 ? error : undefined;
}

function getThrownSaveErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const response = (error as { response?: { data?: unknown } }).response;
  return getSaveErrorMessage(response?.data);
}

const days = Array.from({ length: 31 }, (_, i) =>
  String(i + 1).padStart(2, '0'),
);

function getTodayUtc(): Date {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
}

function getAvailableDobYears(): string[] {
  const todayUtc = getTodayUtc();
  const currentUtcYear = todayUtc.getUTCFullYear();
  const oldestAllowedYear = currentUtcYear - 120;

  return Array.from(
    { length: currentUtcYear - oldestAllowedYear + 1 },
    (_, i) => String(currentUtcYear - i),
  );
}

const years = getAvailableDobYears();

function getDateOfBirthValue({
  day,
  month,
  year,
}: {
  day: string;
  month: string;
  year: string;
}): string {
  return year && month && day ? `${year}-${month}-${day}` : '';
}

function getDateOfBirthValidationMessage(
  incompleteMessage: string,
  {
    day,
    month,
    year,
  }: {
    day: string;
    month: string;
    year: string;
  },
): string | undefined {
  if (!day || !month || !year) {
    return incompleteMessage;
  }

  return getValidationMessage(getDateOfBirthValue({ day, month, year }));
}

function DobPage() {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const saveUserData = useSaveUserData();
  const dateOfBirth = useUserDateOfBirth();
  const isDobRequired = useRequiresDateOfBirth();
  const userId = useUserId();
  const locationHref = useRouterState({
    select: (state) => state.location.href,
  });

  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectTarget = useMemo(() => {
    const locationUrl = new URL(locationHref, 'https://skillarea.local');
    return normalizeDobRedirectTarget(locationUrl.searchParams.get('redirect'));
  }, [locationHref]);

  useEffect(() => {
    if (hasUserEdited || !dateOfBirth) {
      return;
    }

    const parts = dateOfBirth.split('-');
    if (parts.length === 3) {
      setYear(parts[0]);
      setMonth(parts[1]);
      setDay(parts[2]);
    }
  }, [dateOfBirth, hasUserEdited]);

  useEffect(() => {
    if (!userId || isDobRequired) {
      return;
    }

    void navigate({ to: redirectTarget, replace: true });
  }, [isDobRequired, navigate, redirectTarget, userId]);

  const dateOfBirthValue = getDateOfBirthValue({ day, month, year });

  const handleSelectChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
  ) => {
    setHasUserEdited(true);
    setErrorMessage(undefined);
    setter(value);
  };

  const handleSubmit = async (): Promise<void> => {
    setHasUserEdited(true);

    const validationMessage = getDateOfBirthValidationMessage(
      t('dob.incomplete'),
      {
        day,
        month,
        year,
      },
    );
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    setIsSubmitting(true);
    let result: unknown;

    try {
      result = await saveUserData({ dateOfBirth: dateOfBirthValue });
    } catch (error: unknown) {
      setIsSubmitting(false);
      setErrorMessage(getThrownSaveErrorMessage(error) ?? t('dob.saveFailed'));
      return;
    }

    setIsSubmitting(false);

    if (!isSuccessfulResult(result)) {
      setErrorMessage(getSaveErrorMessage(result) ?? t('dob.saveFailed'));
      return;
    }

    setErrorMessage(undefined);
    if (typeof window !== 'undefined') {
      window.location.replace(redirectTarget);
      return;
    }

    await navigate({ to: redirectTarget, replace: true });
  };

  return (
    <div className={styles.pageContainer}>
      <Container>
        <LayoutCard className={styles.card} title={t('dob.title')}>
          <div className={styles.content}>
            <TextBlock color="contrast" weight="600">
              {t('dob.intro')}
            </TextBlock>
            <TextBlock color="secondary">{t('dob.description')}</TextBlock>

            <form
              className={styles.form}
              onSubmit={(event: FormEvent<globalThis.HTMLFormElement>) => {
                event.preventDefault();
                void handleSubmit();
              }}
            >
              <div className={styles.field}>
                <TextBlock color="contrast" weight="600">
                  {t('dob.label')}
                </TextBlock>
                <div className={styles.dateSelects}>
                  <select
                    aria-label={t('dob.day')}
                    className={styles.dateSelect}
                    data-error={!!errorMessage}
                    data-testid="dob-day-select"
                    onChange={(e) => handleSelectChange(setDay, e.target.value)}
                    value={day}
                  >
                    <option disabled value="">
                      {t('dob.day')}
                    </option>
                    {days.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>

                  <select
                    aria-label={t('dob.month')}
                    className={styles.dateSelect}
                    data-error={!!errorMessage}
                    data-testid="dob-month-select"
                    onChange={(e) =>
                      handleSelectChange(setMonth, e.target.value)
                    }
                    value={month}
                  >
                    <option disabled value="">
                      {t('dob.month')}
                    </option>
                    {MONTH_VALUES.map((value) => (
                      <option key={value} value={value}>
                        {t(`dob.months.${value}`)}
                      </option>
                    ))}
                  </select>

                  <select
                    aria-label={t('dob.year')}
                    className={styles.dateSelect}
                    data-error={!!errorMessage}
                    data-testid="dob-year-select"
                    onChange={(e) =>
                      handleSelectChange(setYear, e.target.value)
                    }
                    value={year}
                  >
                    <option disabled value="">
                      {t('dob.year')}
                    </option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                {errorMessage ? (
                  <TextBlock color="error" data-testid="dob-error" size="xs">
                    {errorMessage}
                  </TextBlock>
                ) : null}
              </div>

              <Button
                className={styles.submitButton}
                color="accent"
                data-testid="dob-submit"
                disabled={isSubmitting}
                size="s"
                type="submit"
                variant="outline"
              >
                {t('dob.submit')}
              </Button>
            </form>
          </div>
        </LayoutCard>
      </Container>
    </div>
  );
}
